/**
 * React Query hook for fetching batch jobs with pagination and filtering
 *
 * Provides efficient data fetching for the Jobs tab with support for:
 * - Status filtering
 * - Search by job name/ID
 * - Pagination
 * - Periodic refetching for real-time updates
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { jobService } from '../services/api';
import { JobListResponse, JobFilters } from '../types/job';

const BATCH_JOBS_QUERY_KEY = 'batch-jobs';

/**
 * Hook to fetch jobs for a specific batch with filters and pagination
 */
export function useBatchJobs(
  batchId: string | undefined,
  filters?: Omit<JobFilters, 'batch_id'>,
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
  }
): UseQueryResult<JobListResponse, Error> {
  return useQuery({
    queryKey: [BATCH_JOBS_QUERY_KEY, batchId, filters],
    queryFn: () => {
      if (!batchId) {
        throw new Error('Batch ID is required');
      }
      return jobService.list({
        batch_id: batchId,
        ...filters,
      });
    },
    enabled: !!batchId && (options?.enabled !== false),
    staleTime: 2000, // 2 seconds - data is considered fresh
    refetchInterval: options?.refetchInterval || 2000, // Refetch every 2 seconds for real-time updates
    refetchOnWindowFocus: true,
  });
}
